export type {
  Breadcrumb,
  SdkInfo,
  Event,
  Exception,
  SendFeedbackParams,
  SeverityLevel,
  Span,
  StackFrame,
  Stacktrace,
  Thread,
  User,
  UserFeedback,
  ErrorEvent,
  TransactionEvent,
  Metric,
} from '@sentry/core';

export {
  addBreadcrumb,
  addIntegration,
  captureException,
  captureEvent,
  captureFeedback,
  captureMessage,
  Scope,
  setAttribute,
  setAttributes,
  setContext,
  setExtra,
  setExtras,
  setTag,
  setTags,
  setUser,
  startInactiveSpan,
  startSpan,
  startSpanManual,
  getActiveSpan,
  getRootSpan,
  withActiveSpan,
  suppressTracing,
  spanToJSON,
  spanIsSampled,
  setMeasurement,
  getCurrentScope,
  getGlobalScope,
  getIsolationScope,
  getClient,
  setCurrentClient,
  addEventProcessor,
  lastEventId,
  consoleSandbox,
  instrumentOpenAiClient,
  instrumentAnthropicAiClient,
  instrumentGoogleGenAIClient,
  createLangChainCallbackHandler,
  instrumentLangGraph,
  instrumentStateGraphCompile,
  addConsoleInstrumentationFilter,
} from '@sentry/core';

export type {
  OpenAiClient,
  OpenAiOptions,
  InstrumentedMethod,
  AnthropicAiClient,
  AnthropicAiOptions,
  AnthropicAiInstrumentedMethod,
  AnthropicAiResponse,
  GoogleGenAIClient,
  GoogleGenAIChat,
  GoogleGenAIOptions,
  LangChainOptions,
  LangChainIntegration,
  LangGraphOptions,
  LangGraphIntegration,
  CompiledGraph,
} from '@sentry/core';

export {
  ErrorBoundary,
  withErrorBoundary,
  createReduxEnhancer,
  Profiler,
  useProfiler,
  withProfiler,
} from '@sentry/react';

export {
  logger,
  consoleLoggingIntegration,
  featureFlagsIntegration,
  type FeatureFlagsIntegration,
  metrics,
} from '@sentry/browser';

export * from './integrations/exports';

export { SDK_NAME, SDK_VERSION } from './version';
export type { ReactNativeOptions, NativeLogEntry } from './options';
export { ReactNativeClient } from './client';

export {
  init,
  wrap,
  nativeCrash,
  flush,
  close,
  withScope,
  crashedLastRun,
  appLoaded,
  extendAppStart,
  getExtendedAppStartSpan,
  finishExtendedAppStart,
  pauseAppHangTracking,
  resumeAppHangTracking,
} from './sdk';
export { TouchEventBoundary, withTouchEventBoundary } from './touchevents';
export { NavigationContainer } from './NavigationContainer';
export type { FontStyle, NavigationTheme, SentryNavigationContainerProps } from './NavigationContainer';
export { GlobalErrorBoundary, withGlobalErrorBoundary } from './GlobalErrorBoundary';
export type { GlobalErrorBoundaryProps } from './GlobalErrorBoundary';

export {
  reactNativeTracingIntegration,
  getCurrentReactNativeTracingIntegration,
  getReactNativeTracingIntegration,
  reactNavigationIntegration,
  reactNativeNavigationIntegration,
  sentryTraceGesture,
  TimeToInitialDisplay,
  TimeToFullDisplay,
  startTimeToInitialDisplaySpan,
  startTimeToFullDisplaySpan,
  reportFullyDisplayed,
  startIdleNavigationSpan,
  startIdleSpan,
  getDefaultIdleNavigationSpanOptions,
  createTimeToFullDisplay,
  createTimeToInitialDisplay,
  wrapExpoRouter,
  expoRouterIntegration,
  wrapExpoRouterErrorBoundary,
  wrapExpoImage,
  wrapExpoAsset,
} from './tracing';

export type { TimeToDisplayProps, ExpoRouter, ExpoRouterErrorBoundaryProps, ExpoImage, ExpoAsset } from './tracing';

export { Mask, Unmask } from './replay/CustomMask';

/** @deprecated The `FeedbackButton` component will be removed in a future major version. */
export { FeedbackButton } from './feedback/FeedbackButton';
export { FeedbackForm } from './feedback/FeedbackForm';
export { showFeedbackForm, enableFeedbackOnShake, disableFeedbackOnShake } from './feedback/FeedbackFormManager';
/** @deprecated `showFeedbackButton` will be removed in a future major version. */
export { showFeedbackButton } from './feedback/FeedbackFormManager';
/** @deprecated `hideFeedbackButton` will be removed in a future major version. */
export { hideFeedbackButton } from './feedback/FeedbackFormManager';

/** @deprecated Use `FeedbackForm` instead. */
export { FeedbackForm as FeedbackWidget } from './feedback/FeedbackForm';
/** @deprecated Use `showFeedbackForm` instead. */
export { showFeedbackForm as showFeedbackWidget } from './feedback/FeedbackFormManager';

export { getDataFromUri } from './wrapper';

export {
  getActiveTurboModuleCall,
  getTurboModuleCallStack,
  popTurboModuleCall,
  pushTurboModuleCall,
  wrapTurboModule,
} from './turbomodule';
export type { TurboModuleCall, TurboModuleCallKind } from './turbomodule';
